import React from 'react'
import { supabase } from '@/lib/supabase'
import {
  addResultImages,
  deleteResult,
  fetchResultsThread,
  postResult,
  type ResultEntry,
} from '../services/resultsService'
import { uploadPublicFile } from '@/modules/upload/services/uploadService'

type CurrentUser = {
  name: string
  avatarUrl?: string | null
} | null

export function useResultsThread(
  opportunityId: number | null,
  currentUser: CurrentUser,
  active: boolean,
) {
  const [loading, setLoading] = React.useState(false)
  const [sending, setSending] = React.useState(false)
  const [loadedOnce, setLoadedOnce] = React.useState(false)
  const [results, setResults] = React.useState<ResultEntry[]>([])

  const load = React.useCallback(async () => {
    if (!opportunityId || !active) {
      setResults([])
      setLoadedOnce(false)
      return
    }

    setLoading(true)
    try {
      const data = await fetchResultsThread(opportunityId)
      setResults(data)
      setLoadedOnce(true)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('useResultsThread load error', error)
    } finally {
      setLoading(false)
    }
  }, [active, opportunityId])

  React.useEffect(() => {
    if (!opportunityId || !active) {
      setResults([])
      setLoadedOnce(false)
      return
    }

    let alive = true

    ;(async () => {
      await load()
      if (!alive) return
    })()

    return () => {
      alive = false
    }
  }, [active, load, opportunityId])

  React.useEffect(() => {
    if (!opportunityId || !active) return

    let timer: any = null

    const scheduleReload = () => {
      try {
        if (timer) clearTimeout(timer)
      } catch {
        // ignore
      }
      timer = setTimeout(() => {
        load().catch(() => {
          // erros são tratados dentro de load
        })
      }, 500)
    }

    const channel = supabase
      .channel(`results_thread:${opportunityId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'opportunity_results',
          filter: `opportunity_id=eq.${opportunityId}`,
        } as any,
        () => {
          scheduleReload()
        },
      )
      .subscribe()

    return () => {
      try {
        supabase.removeChannel(channel)
      } catch {
        // ignore
      }
      try {
        if (timer) clearTimeout(timer)
      } catch {
        // ignore
      }
    }
  }, [active, load, opportunityId])

  React.useEffect(() => {
    if (!opportunityId || !active) return

    const interval = setInterval(() => {
      load().catch(() => {
        // silencioso
      })
    }, 1000)

    return () => {
      clearInterval(interval)
    }
  }, [active, load, opportunityId])

  const sendResult = React.useCallback(
    async (files: File[]) => {
      if (!opportunityId || !currentUser || !active) return

      const rawList = Array.isArray(files) ? files : []
      const images = rawList.filter(
        (file) => file && typeof file.type === 'string' && file.type.startsWith('image/'),
      )
      if (!images.length) return

      const MAX_BYTES = 1_000_000
      const accepted: File[] = []
      images.forEach((file) => {
        if (file.size <= MAX_BYTES) {
          accepted.push(file)
        }
      })

      if (!accepted.length) return

      setSending(true)
      try {
        const saved = await postResult(opportunityId, currentUser.name, currentUser.avatarUrl ?? null)
        if (!saved) {
          await load()
          return
        }

        const uploaded: {
          file: File
          url: string
          bucket: string
          path: string
        }[] = []

        for (const file of accepted) {
          try {
            const { url, bucket, path } = await uploadPublicFile(
              file,
              `results/opportunities/${opportunityId}/${saved.id}`,
            )
            uploaded.push({ file, url, bucket, path })
          } catch (uploadErr) {
            // eslint-disable-next-line no-console
            console.error('[Results] upload image error', uploadErr)
          }
        }

        if (uploaded.length === 0) {
          try {
            await deleteResult(saved.id)
          } catch (cleanupErr) {
            // eslint-disable-next-line no-console
            console.error('[Results] cleanup empty result error', cleanupErr)
          }
          throw new Error(
            'Nenhuma imagem foi salva. Verifique sua conexão e tente novamente.',
          )
        }

        try {
          await addResultImages(
            saved.id,
            uploaded.map((item) => ({
              url: item.url,
              storageBucket: item.bucket,
              storagePath: item.path,
              contentType: item.file.type || null,
              sizeBytes:
                typeof item.file.size === 'number' ? item.file.size : null,
            })),
          )
        } catch (attachErr) {
          // eslint-disable-next-line no-console
          console.error('[Results] addResultImages error', attachErr)
        }

        await load()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[Results] sendResult error', error)
        try {
          await load()
        } catch {
          // ignore
        }
        throw error
      } finally {
        setSending(false)
      }
    },
    [active, currentUser, load, opportunityId],
  )

  const deleteResultFn = React.useCallback(
    async (resultId: number) => {
      if (!opportunityId || !active) return
      const id = Number(resultId)
      if (!Number.isFinite(id)) return

      setResults((prev) => prev.filter((r) => Number(r.id) !== id))

      try {
        await deleteResult(id)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[Results] deleteResult error', error)
      } finally {
        load().catch(() => {
          // erro já tratado dentro de load
        })
      }
    },
    [active, load, opportunityId],
  )

  return {
    loading,
    loadedOnce,
    sending,
    results,
    sendResult,
    deleteResult: deleteResultFn,
    reload: load,
  }
}

export type ResultsThreadHook = ReturnType<typeof useResultsThread>
